/**
 * UNIFIED MODEL CATALOG SERVICE
 *
 * Single source of truth for querying model metadata with provider tier filtering.
 */

import {
  MODEL_PRICING,
  type ModelPricing,
  type ModelCapabilities,
  type ProviderTier,
  type ModelFamily,
  type ModelCapability,
  deriveCapabilitiesFromParams,
  VALID_PROVIDER_TIERS,
} from './shared-pricing-config';

// ========================================
// TYPES
// ========================================

export interface ProviderService {
  serviceId: string;
  tier: ProviderTier;
  serviceModelId: string;
  isActive: boolean;
  priority?: number;
}

export interface ModelTierAvailability {
  modelId: string;
  availableTiers: ProviderTier[];
  providersByTier: Partial<Record<ProviderTier, ProviderService[]>>;
}

export interface ModelCatalogEntry extends ModelPricing {
  modelId: string;
  availableTiers: ProviderTier[];
  providersByTier: Partial<Record<ProviderTier, ProviderService[]>>;
  capabilities: ModelCapability[];
  routing?: ModelCapabilities;
}

export interface ModelQueryFilter {
  family?: ModelFamily | ModelFamily[];
  requiredTiers?: ProviderTier[];
  anyTier?: ProviderTier[];
  capabilities?: ModelCapability[];
  featuredOnly?: boolean;
  minContextTokens?: number;
  maxInputPrice?: number;
  includeNoTier?: boolean;
}

export interface ModelSortOptions {
  sortBy?: 'price' | 'context' | 'latency' | 'family' | 'modelId';
  sortOrder?: 'asc' | 'desc';
}

// ========================================
// MODEL CATALOG SERVICE
// ========================================

export class ModelCatalogService {
  private tierAvailabilityMap: Map<string, ModelTierAvailability>;

  constructor(tierAvailability?: ModelTierAvailability[]) {
    this.tierAvailabilityMap = new Map();
    if (tierAvailability) {
      tierAvailability.forEach(entry => {
        this.tierAvailabilityMap.set(entry.modelId, entry);
      });
    }
  }

  setTierAvailability(modelId: string, availability: ModelTierAvailability): void {
    this.tierAvailabilityMap.set(modelId, availability);
  }

  bulkSetTierAvailability(availabilityList: ModelTierAvailability[]): void {
    availabilityList.forEach(entry => {
      this.tierAvailabilityMap.set(entry.modelId, entry);
    });
  }

  getTierAvailability(modelId: string): ModelTierAvailability | undefined {
    return this.tierAvailabilityMap.get(modelId);
  }

  getModel(modelId: string): ModelCatalogEntry | undefined {
    const pricing = MODEL_PRICING[modelId];
    if (!pricing) return undefined;

    const tierAvailability = this.tierAvailabilityMap.get(modelId);
    const capabilities = deriveCapabilitiesFromParams(pricing.supportedParams, pricing.family);
    const routing = pricing.routing;

    // Temporary fallback: If no tier data exists, assign default tiers based on price
    // This will be replaced with real database data later
    let availableTiers: ProviderTier[] = tierAvailability?.availableTiers || [];
    if (availableTiers.length === 0) {
      // Default tier assignment based on input price. Models at or below the
      // $3/M input cutoff are eligible for all tiers (economy/standard get a
      // real discount off this same cheap raw cost, premium pays full price);
      // above that cutoff, only premium is offered. Use the 'premium' literal
      // rather than an index into VALID_PROVIDER_TIERS — the array's length
      // and ordering aren't guaranteed to keep 'premium' at any fixed slot.
      const inputPrice = pricing.inputPricePerMillion;
      if (inputPrice <= 3) {
        availableTiers = [...VALID_PROVIDER_TIERS]; // Cheap - all tiers
      } else {
        availableTiers = ['premium']; // Expensive - premium only
      }
    }

    return {
      modelId,
      ...pricing,
      availableTiers,
      providersByTier: tierAvailability?.providersByTier || {},
      capabilities,
      routing,
    };
  }

  listModels(filter?: ModelQueryFilter, sort?: ModelSortOptions): ModelCatalogEntry[] {
    const allModelIds = Object.keys(MODEL_PRICING);
    let results: ModelCatalogEntry[] = [];

    for (const modelId of allModelIds) {
      const entry = this.getModel(modelId);
      if (!entry) continue;
      if (!this.matchesFilter(entry, filter)) continue;
      results.push(entry);
    }

    if (sort?.sortBy) {
      results = this.sortModels(results, sort);
    }

    return results;
  }

  getModelsForTiers(tiers: ProviderTier[]): ModelCatalogEntry[] {
    return this.listModels({ anyTier: tiers });
  }

  getModelsByFamily(family: ModelFamily): ModelCatalogEntry[] {
    return this.listModels({ family });
  }

  getFeaturedModels(tiers?: ProviderTier[]): ModelCatalogEntry[] {
    return this.listModels({ featuredOnly: true, anyTier: tiers });
  }

  getCheapestModels(limit: number = 10, tiers?: ProviderTier[]): ModelCatalogEntry[] {
    const sorted = this.listModels(
      { anyTier: tiers },
      { sortBy: 'price', sortOrder: 'asc' }
    );
    return sorted.slice(0, limit);
  }

  getLargestContextModels(limit: number = 10, tiers?: ProviderTier[]): ModelCatalogEntry[] {
    const sorted = this.listModels(
      { anyTier: tiers },
      { sortBy: 'context', sortOrder: 'desc' }
    );
    return sorted.slice(0, limit);
  }

  isModelAvailableOnTier(modelId: string, tier: ProviderTier): boolean {
    const availability = this.tierAvailabilityMap.get(modelId);
    return availability?.availableTiers.includes(tier) || false;
  }

  getAvailableTiers(modelId: string): ProviderTier[] {
    const availability = this.tierAvailabilityMap.get(modelId);
    return availability?.availableTiers || [];
  }

  getProviderServices(modelId: string, tier: ProviderTier): ProviderService[] {
    const availability = this.tierAvailabilityMap.get(modelId);
    return availability?.providersByTier[tier] || [];
  }

  private matchesFilter(entry: ModelCatalogEntry, filter?: ModelQueryFilter): boolean {
    if (!filter) return true;

    if (filter.family) {
      const families = Array.isArray(filter.family) ? filter.family : [filter.family];
      if (!families.includes(entry.family)) return false;
    }

    if (filter.requiredTiers && filter.requiredTiers.length > 0) {
      const hasAllTiers = filter.requiredTiers.every(tier =>
        entry.availableTiers.includes(tier)
      );
      if (!hasAllTiers) return false;
    }

    if (filter.anyTier && filter.anyTier.length > 0) {
      const hasAnyTier = filter.anyTier.some(tier =>
        entry.availableTiers.includes(tier)
      );
      if (!hasAnyTier && !(filter.includeNoTier && entry.availableTiers.length === 0)) {
        return false;
      }
    }

    if (filter.capabilities && filter.capabilities.length > 0) {
      const hasAllCapabilities = filter.capabilities.every(cap =>
        entry.capabilities.includes(cap)
      );
      if (!hasAllCapabilities) return false;
    }

    if (filter.featuredOnly && !entry.featured) return false;

    if (filter.minContextTokens && entry.maxContextTokens < filter.minContextTokens) {
      return false;
    }

    if (filter.maxInputPrice && entry.inputPricePerMillion > filter.maxInputPrice) {
      return false;
    }

    return true;
  }

  private sortModels(models: ModelCatalogEntry[], sort: ModelSortOptions): ModelCatalogEntry[] {
    const { sortBy = 'modelId', sortOrder = 'asc' } = sort;
    const direction = sortOrder === 'asc' ? 1 : -1;

    return models.sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'price':
          comparison = a.inputPricePerMillion - b.inputPricePerMillion;
          break;
        case 'context':
          comparison = a.maxContextTokens - b.maxContextTokens;
          break;
        case 'latency':
          comparison = (a.latency || 999) - (b.latency || 999);
          break;
        case 'family':
          comparison = a.family.localeCompare(b.family);
          break;
        case 'modelId':
        default:
          comparison = a.modelId.localeCompare(b.modelId);
          break;
      }

      return comparison * direction;
    });
  }
}

// ========================================
// NOTE: Singleton pattern removed
// ========================================
// Use Jotai atoms instead (see app/src/shared/hooks/useModelCatalog.atom.ts)
// Direct imports should use the ModelCatalogService class directly

